import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tooltip,
  Select,
  MenuItem,
  SelectChangeEvent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
} from 'recharts';
import BaseBlock from './BaseBlock';
import { format } from 'date-fns';
import {
  Send as SendIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useAccessToken, useUserState } from '@features/user';
import { useAssignForms, useFormDetail } from '@features/forms';
import { useSnackbar } from 'notistack';
import SlideSidePane from '../../../Views/Shared/SlideSidePane';
import FormDetailView from '../../Forms/FormDetailView';
import APIService from '../../../services/APIService';
import { Patient } from '../../../types/types';
import SentryService from '../../../services/SentryService';

interface TestScore {
  date: string;
  score: number;
  notes?: string;
  interpretation?: string;
  form_session_id?: string; // Links to the form submission that created this score
}

interface TestMetadata {
  form_id?: string;
  form_type: string;
  grading_matrix: {
    ranges: {
      min: number;
      max: number;
      interpretation: string;
    }[];
  };
}

interface TestModuleData {
  current_data: {
    data?: TestScore[];
  };
  form_metadata: TestMetadata;
  type: 'test';
  patient_module_id: string;
}

interface TestBlockProps {
  moduleData: TestModuleData;
  title: string;
  patientId: string;
  patient?: Patient;
}

const getScoreSeverity = (score: number, metadata: TestMetadata) => {
  const interpretation = metadata.grading_matrix.ranges.find(
    range => score >= range.min && score <= range.max,
  );
  return {
    severity: interpretation?.interpretation || 'Unknown',
    description: 'No interpretation available',
  };
};

const TestBlock: React.FC<TestBlockProps> = ({ moduleData, title, patientId, patient }) => {
  const metadata = moduleData.form_metadata;
  const scores = moduleData.current_data.data || [];
  const userState = useUserState();
  const { getAccessToken } = useAccessToken();
  const { enqueueSnackbar } = useSnackbar();
  const { mutate: assignForms } = useAssignForms();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newScore, setNewScore] = useState('');
  const [newDate, setNewDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [editingScore, setEditingScore] = useState<TestScore | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isFormDetailsOpen, setIsFormDetailsOpen] = useState(false);
  const [selectedFormSessionId, setSelectedFormSessionId] = useState<string | null>(null);
  const isBlocked = !patient?.dob;

  // Fetch form details when a form session is selected
  const { data: formDetail, isLoading: isLoadingFormDetail } = useFormDetail(
    { form_session_id: selectedFormSessionId || '' },
    { skip: !selectedFormSessionId },
  );

  const handleSendForm = () => {
    if (!metadata.form_id) {
      enqueueSnackbar('No form ID available', { variant: 'error' });
      return;
    }

    assignForms(
      {
        patient_id: patientId,
        form_ids: [metadata.form_id],
      },
      {
        onSuccess: () => {
          enqueueSnackbar('Form sent successfully!', { variant: 'success' });
        },
        onError: error => {
          console.error('Error sending form:', error);
          enqueueSnackbar('Failed to send form', { variant: 'error' });
        },
      },
    );
  };

  // Shared function to update patient module data
  const updatePatientModule = async (updatedScores: TestScore[], successMessage: string) => {
    try {
      const accessToken = await getAccessToken();
      const response = await APIService.makeAPIPostRequest({
        requestString: '/patient/updatePatientModule',
        accessToken,
        body: {
          patient_id: patientId,
          patient_module_id: moduleData.patient_module_id,
          data: { data: updatedScores },
        },
      });

      if (response.ok) {
        moduleData.current_data.data = updatedScores;
        enqueueSnackbar(successMessage, { variant: 'success' });
        return true;
      } else {
        throw new Error('Failed to update patient module');
      }
    } catch (error) {
      console.error('Error updating patient module:', error);
      enqueueSnackbar('Failed to update scores', { variant: 'error' });
      SentryService.logEvent('Error updating patient module', {
        level: 'error',
        tags: {
          patient_id: patientId || '',
        },
        extra: {
          error: error,
          operation: successMessage,
        },
      });
      return false;
    }
  };

  const handleAddScore = async () => {
    const scoreNum = parseFloat(newScore);
    if (isNaN(scoreNum)) {
      enqueueSnackbar('Please enter a valid score', { variant: 'error' });
      return;
    }

    const newScoreEntry: TestScore = {
      date: new Date(newDate + 'T12:00:00.000Z').toISOString(),
      score: scoreNum,
    };

    const updatedScores = [...(moduleData.current_data.data || []), newScoreEntry];

    const success = await updatePatientModule(updatedScores, 'Score added successfully');
    if (success) {
      setIsAddDialogOpen(false);
      setNewScore('');
      setNewDate(format(new Date(), 'yyyy-MM-dd'));
    }
  };

  const handleEditClick = (score: TestScore) => {
    // Find the actual index in the original data array
    const actualIndex =
      moduleData.current_data.data?.findIndex(
        s => s.date === score.date && s.score === score.score,
      ) ?? -1;

    if (actualIndex !== -1) {
      setEditingScore(score);
      setEditingIndex(actualIndex);
      setNewScore(score.score.toString());
      setNewDate(format(new Date(score.date), 'yyyy-MM-dd'));
    }
  };

  const handleCloseDialog = () => {
    setIsAddDialogOpen(false);
    setEditingScore(null);
    setEditingIndex(null);
    setNewScore('');
    setNewDate(format(new Date(), 'yyyy-MM-dd'));
  };

  const handleViewSubmission = () => {
    if (editingScore?.form_session_id) {
      setSelectedFormSessionId(editingScore.form_session_id);
      setIsFormDetailsOpen(true);
    }
  };

  const handleFormDetailsClose = () => {
    setIsFormDetailsOpen(false);
    setSelectedFormSessionId(null);
  };

  const handleUpdateScore = async () => {
    if (editingIndex === null) return;

    const scoreNum = parseFloat(newScore);
    if (isNaN(scoreNum)) {
      enqueueSnackbar('Please enter a valid score', { variant: 'error' });
      return;
    }

    const updatedScores = [...(moduleData.current_data.data || [])];
    updatedScores[editingIndex] = {
      ...updatedScores[editingIndex],
      date: new Date(newDate + 'T12:00:00.000Z').toISOString(),
      score: scoreNum,
    };

    const success = await updatePatientModule(updatedScores, 'Score updated successfully');
    if (success) {
      handleCloseDialog();
    }
  };

  const handleDeleteScore = async () => {
    if (editingIndex === null) return;

    const updatedScores =
      moduleData.current_data.data?.filter((_, index) => index !== editingIndex) || [];

    const success = await updatePatientModule(updatedScores, 'Score deleted successfully');
    if (success) {
      handleCloseDialog();
    }
  };

  // Sort scores by date before using them
  const sortedScores = useMemo(() => {
    return [...scores].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [scores]);

  // Update to use sortedScores instead of scores
  const latestScore = useMemo(() => sortedScores[sortedScores.length - 1], [sortedScores]);
  const recentScores = useMemo(
    () =>
      sortedScores.slice(-12).map(score => ({
        ...score,
        formattedDate: format(new Date(score.date), 'MMM d'),
      })),
    [sortedScores],
  );

  const severity = useMemo(
    () => (latestScore ? getScoreSeverity(latestScore.score, metadata) : null),
    [latestScore, metadata],
  );

  return (
    <BaseBlock
      title={title}
      actions={
        userState?.featureFlags?.forms && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setIsAddDialogOpen(true)}
            >
              Add Score
            </Button>
            {metadata.form_id && (
              <Tooltip
                title={isBlocked ? 'Patient date of birth is required to send forms' : ''}
                arrow
              >
                <span>
                  {' '}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SendIcon />}
                    onClick={handleSendForm}
                    disabled={isBlocked}
                  >
                    Send
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>
        )
      }
    >
      {scores.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 250,
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No scores yet
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, height: 250 }}>
          {/* Left Column: Score Card + Legend */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              width: 200,
            }}
          >
            {/* Latest Score Card */}
            <Card>
              <CardContent
                sx={{
                  py: 1,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Latest Score
                </Typography>
                <Typography variant="h4" component="div">
                  {latestScore.score}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {format(new Date(latestScore.date), 'MMM d, yyyy')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Severity: {severity?.severity}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Score History Graph */}
          <Box sx={{ flex: 1 }}>
            <ResponsiveContainer>
              <LineChart
                data={recentScores}
                margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                onClick={(data: any) => {
                  if (data && data.activePayload && data.activePayload[0]) {
                    const clickedScore = data.activePayload[0].payload;
                    handleEditClick(clickedScore);
                  }
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="formattedDate" tick={{ fontSize: 12 }} />
                <YAxis
                  domain={[
                    metadata.grading_matrix.ranges[0].min,
                    metadata.grading_matrix.ranges[metadata.grading_matrix.ranges.length - 1].max,
                  ]}
                  tick={{ fontSize: 12 }}
                />
                <ChartTooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <Card sx={{ p: 1, bgcolor: 'background.paper' }}>
                          <Typography variant="body2">
                            Date: {format(new Date(data.date), 'MMMM d, yyyy')}
                          </Typography>
                          <Typography variant="body2">Score: {data.score}</Typography>
                          {data.notes && (
                            <Typography variant="body2">Note: {data.notes}</Typography>
                          )}
                        </Card>
                      );
                    }
                    return null;
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="score"
                  stroke="#8884d8"
                  strokeWidth={2}
                  dot={{ r: 4, cursor: 'pointer' }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      )}

      <Dialog open={isAddDialogOpen || editingScore !== null} onClose={handleCloseDialog}>
        <DialogTitle>{editingScore ? 'Edit Score' : 'Add New Score'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Score"
              type="number"
              value={newScore}
              onChange={e => setNewScore(e.target.value)}
              inputProps={{
                min: metadata.grading_matrix.ranges[0].min,
                max: metadata.grading_matrix.ranges[metadata.grading_matrix.ranges.length - 1].max,
              }}
              fullWidth
            />
            <TextField
              label="Date"
              type="date"
              value={newDate}
              onChange={e => setNewDate(e.target.value)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          {editingScore && (
            <>
              <Tooltip
                title={!editingScore.form_session_id ? 'This score was manually added' : ''}
                arrow
              >
                <span style={{ marginRight: 'auto' }}>
                  <Button
                    onClick={handleViewSubmission}
                    startIcon={<VisibilityIcon />}
                    disabled={!editingScore.form_session_id}
                  >
                    View Submission
                  </Button>
                </span>
              </Tooltip>
              <Button onClick={handleDeleteScore} color="error">
                Delete
              </Button>
            </>
          )}
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={editingScore ? handleUpdateScore : handleAddScore} variant="contained">
            {editingScore ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <SlideSidePane
        open={isFormDetailsOpen}
        onClose={handleFormDetailsClose}
        title={formDetail?.form_definition?.title || 'Form Submission Details'}
      >
        {isLoadingFormDetail ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography>Loading form details...</Typography>
          </Box>
        ) : formDetail ? (
          <Box sx={{ p: 3 }}>
            <FormDetailView
              formDefinition={formDetail.form_definition}
              submissionData={formDetail.submission_data}
              mode="condensed"
            />
          </Box>
        ) : (
          <Box sx={{ p: 3 }}>
            <Typography>No form details available</Typography>
          </Box>
        )}
      </SlideSidePane>
    </BaseBlock>
  );
};

export default TestBlock;
